import classNames from 'classnames'
import { useMemo } from 'react'

import { QUERY_GET_UNIT_ACTIVATION_TEMPLATES, useDeleteUnitActivationTemplate } from '@apps-common/apis/portal/unit-activations'
import { ButtonRoundSm, INPUT_INFO_SM_NO_BORDER, InputInfoSM } from '@apps-common/components/elements'
import { commonIcons } from '@apps-common/config/icons'
import { queryClient } from '@apps-common/lib'
import { IActivationServiceRequestTemplateModel } from '@apps-common/types/portal/service-request'
import { IUnitPlanModel } from '@apps-common/types/portal/unit'

interface IProps {
  template: IActivationServiceRequestTemplateModel
  plans?: IUnitPlanModel[]
  onSelected: (data: IActivationServiceRequestTemplateModel) => void
  clearSelected: () => void
}

export const TemplateCard: React.FC<IProps> = ({ template, plans, onSelected, clearSelected }) => {
  const { id, name, mCentre, assignBureau, bureau, assignTech, tech, useLineSupervision, unitPlanId } = template

  const { mutate: deleteTemplate, isPending: deleteIsPending } = useDeleteUnitActivationTemplate(true, () => {
    queryClient.invalidateQueries({ queryKey: QUERY_GET_UNIT_ACTIVATION_TEMPLATES() })
    clearSelected()
  })

  const plan = useMemo(() => plans?.find((p) => p.unitPlanId === unitPlanId), [plans])

  return (
    <button
      type='button'
      className='relative w-full text-left bg-surface text-text border border-border rounded-2xl p-4 flex flex-col gap-3'
    >
      <div className='w-full flex items-start justify-between gap-4'>
        <div className='border-b border-border pl-2 pb-3 capitalize max-w-[300px] text-nowrap text-ellipsis overflow-hidden text-[0.9em]'>
          {name}
        </div>
        <div className='flex items-center justify-end gap-2'>
          <ButtonRoundSm
            type='button'
            disabled={deleteIsPending}
            callback={(e) => {
              e?.stopPropagation()
              deleteTemplate({ model: template })
            }}
            className='action-error'
            tooltip={{
              id: 'delete-unit-activation-template',
              message: 'Delete template',
              place: 'top'
            }}
          >
            <commonIcons.delete />
          </ButtonRoundSm>
          <ButtonRoundSm
            type='div'
            disabled={deleteIsPending}
            callback={(e) => {
              e?.stopPropagation()
              if (!deleteIsPending) {
                onSelected(template)
              }
            }}
            className='action-success'
            tooltip={{
              id: 'create-unit-activation-from-template',
              message: 'Create from template',
              place: 'top'
            }}
          >
            <commonIcons.arrowAngled />
          </ButtonRoundSm>
        </div>
      </div>
      <div className='pt-2 grid sm:grid-cols-2 gap-[10px] w-full'>
        <InputInfoSM
          id='monitoringCentre'
          label='Monitoring Centre'
          showLabel
          labelClassName='ml-0 mb-1 rounded-sm jost-medium'
          className={INPUT_INFO_SM_NO_BORDER}
          hideNoEditIcon
          value={mCentre}
        />
        <InputInfoSM
          id='bureau'
          label='Bureau'
          showLabel
          labelClassName='ml-0 mb-1 rounded-sm jost-medium'
          className={INPUT_INFO_SM_NO_BORDER}
          hideNoEditIcon
          value={assignBureau ? bureau : <commonIcons.dash />}
        />
        <InputInfoSM
          id='technician'
          label='Technician'
          showLabel
          labelClassName='ml-0 mb-1 rounded-sm jost-medium'
          className={INPUT_INFO_SM_NO_BORDER}
          hideNoEditIcon
          value={assignTech ? tech : <commonIcons.dash />}
        />
        <InputInfoSM
          id='lineSupervision'
          label='line supervision'
          showLabel
          labelClassName='ml-0 mb-1 rounded-sm jost-medium'
          className={INPUT_INFO_SM_NO_BORDER}
          hideNoEditIcon
          value={useLineSupervision ? 'Enabled' : 'Disabled'}
        />
        <InputInfoSM
          id='plan'
          label='Plan'
          showLabel
          labelClassName='ml-0 mb-1 rounded-sm jost-medium'
          className={classNames(INPUT_INFO_SM_NO_BORDER, 'capitalize')}
          hideNoEditIcon
          value={plan ? plan.unitPlanName?.toLowerCase() : <commonIcons.dash />}
        />
      </div>
    </button>
  )
}
